import type { ReactNode } from "react"

import { cn } from "../../lib/utils"

// The one pill primitive for status / count chips across the app. A tone maps to
// a semantic tint+text pair so it auto-switches in dark mode; `sm` is for inline
// grid chips, `md` for header badges. Leading dots/icons go in as children.
export type BadgeTone = "success" | "warning" | "destructive" | "info" | "muted" | "brand"
type BadgeSize = "sm" | "md"

const TONE_CLASSES: Record<BadgeTone, string> = {
  success: "bg-success/10 text-success",
  warning: "bg-warning/10 text-warning",
  destructive: "bg-destructive/10 text-destructive",
  info: "bg-info/10 text-info",
  muted: "bg-muted text-muted-foreground",
  brand: "bg-brand/10 text-brand",
}

const SIZE_CLASSES: Record<BadgeSize, string> = {
  sm: "gap-1 px-2 py-0.5 text-[11px]",
  md: "gap-1.5 px-2.5 py-1 text-xs",
}

type StatusBadgeProps = {
  tone?: BadgeTone
  size?: BadgeSize
  className?: string
  title?: string | undefined
  children: ReactNode
}

export function StatusBadge({
  tone = "muted",
  size = "md",
  className,
  title,
  children,
}: StatusBadgeProps) {
  return (
    <span
      title={title}
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-full font-medium tabular-nums",
        SIZE_CLASSES[size],
        TONE_CLASSES[tone],
        className
      )}
    >
      {children}
    </span>
  )
}
